import React, { useEffect, useState, Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
type TaxItem = {
  num: string;
  anchor: string;
  name: string;
  definition: string;
  feelsLike: string;
  whereShowsUp: string;
  whatItCauses: string;
  commonMisread: string;
  validate: string[];
};
export function WhatsWrong() {
  const [expandedTax, setExpandedTax] = useState<number | null>(null);
  const location = useLocation();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const taxes: TaxItem[] = [{
    num: '1',
    anchor: 'dependence',
    name: 'Dependence Tax',
    definition: 'Too much still depends on the founder or a few key people.',
    feelsLike: 'If the right person is out, progress slows or stops.',
    whereShowsUp: 'Qualification, approvals, handoffs, exception handling, delivery judgment, and expansion decisions that still depend on a few people carrying the logic.',
    whatItCauses: 'Slower movement, inconsistent decisions, stalled handoffs, founder pull, and revenue loss or missed upside whenever the right person is not in the loop.',
    commonMisread: 'It looks like a people problem. It is often trapped logic that never made it into shared rules, workflows, or decision structure.',
    validate: ['Where important decisions still rely on memory, interpretation, or one person carrying too much context.']
  }, {
    num: '2',
    anchor: 'opportunity',
    name: 'Opportunity Tax',
    definition: 'Leadership has no room for the future because today keeps eating all the oxygen.',
    feelsLike: 'Strategic work keeps getting pushed because the business cannot run without constant intervention.',
    whereShowsUp: 'Anywhere leadership keeps getting pulled back into conversion issues, customer problems, reporting questions, delivery friction, or escalations instead of redesigning what is next.',
    whatItCauses: 'Strategic delay, reactive leadership, constant context switching, and missed revenue because the business is too busy catching what should not have broken.',
    commonMisread: 'It looks like a planning problem. It is often a load-path problem.',
    validate: ['What keeps routing important work back into the urgent pile.']
  }, {
    num: '3',
    anchor: 'leakage',
    name: 'Leakage Tax',
    definition: 'Revenue and margin leak through exceptions, rework, unclear scope, and work that should have resolved earlier.',
    feelsLike: 'Revenue is coming in, but profitability does not follow the way it should.',
    whereShowsUp: 'Qualification, promises, scoping, handoffs, delivery, retention, and anywhere unmanaged variance keeps entering the system.',
    whatItCauses: 'Bad-fit sales, rework, margin erosion, cleanup, downstream churn, and missed expansion revenue because the business is too busy catching what should not have broken.',
    commonMisread: 'It looks like pricing. Sometimes it is. Often it is unmanaged variance and weak journey logic.',
    validate: ['Where exceptions enter, how they become normal, and where profit is quietly being lost.']
  }, {
    num: '4',
    anchor: 'visibility',
    name: 'Visibility Tax',
    definition: 'The numbers create debate instead of decisions.',
    feelsLike: 'Leaders cannot tell what is true fast enough to move with confidence.',
    whereShowsUp: 'Lead flow, conversion, delivery quality, churn patterns, expansion opportunities, and performance reporting that should guide action but does not.',
    whatItCauses: 'Slow decisions, missed signals, weak learning, debate instead of action, and too much leadership energy spent validating reality before moving to capture revenue.',
    commonMisread: 'It looks like a dashboard problem. It is often a definitions problem.',
    validate: ['Which metrics are not trusted, where definitions drift, and what has to be reconciled manually before decisions can happen.']
  }, {
    num: '5',
    anchor: 'capability',
    name: 'Capability Tax',
    definition: 'Managers cannot carry the load cleanly, so complexity routes upward.',
    feelsLike: 'Leaders spend all day translating, resolving, and stepping back into work that should not need them.',
    whereShowsUp: 'Cross-functional coordination, handoffs, exception handling, customer movement, and day-to-day decisions that managers cannot carry cleanly without escalation.',
    whatItCauses: 'Stalled work, slow execution, too much translation, leadership becoming the routing layer, and a business that cannot scale revenue cleanly through the middle.',
    commonMisread: 'It looks like weak management. It is often unclear ownership and weak decision structure under real complexity.',
    validate: ['Where accountability breaks down under load and where managers do not have the structure needed to carry the weight.']
  }, {
    num: '6',
    anchor: 'technology',
    name: 'Technology Tax',
    definition: 'The stack exists, but it does not create real leverage.',
    feelsLike: 'You keep buying software and still end up doing the work the hard way.',
    whereShowsUp: 'Routing, reporting, workflow execution, automation, AI use cases, and system adoption where the tools exist but the underlying logic is still too unclear.',
    whatItCauses: 'Tool sprawl, brittle automation, workarounds, wasted software spend, and AI that produces output without revenue leverage or upside.',
    commonMisread: 'It looks like the wrong tools. Often the bigger issue is unclear rules, vague ownership, and workflows that were never redesigned.',
    validate: ['Whether the business logic is clear enough for automation or AI to help at all.']
  }];
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taxParam = params.get('tax')?.toLowerCase() ?? '';
    const hash = (location.hash || '').replace('#', '').toLowerCase();
    const target = taxParam || hash;
    if (!target) return;
    const idx = taxes.findIndex(t => t.anchor === target);
    if (idx === -1) return;
    setExpandedTax(idx);
    setTimeout(() => {
      const el = document.getElementById(target);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }, [location.search, location.hash]);
  return <main id="top" className="w-full" data-id="element-623">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-624">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-625">
          <BlueprintGrid data-id="element-626" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-627">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-628">
            What&apos;s Wrong
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-629">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-630">
              If too much revenue is getting stuck, lost, or missed across the
              journey,
            </span>{' '}
            <span className="text-navy" data-id="element-631">
              the underlying failure usually shows up in a few recognizable
              patterns.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-632">
            <p data-id="element-633">
              You feel it when leads stall, handoffs break, delivery inherits
              shaky promises, expansion gets missed, and growth starts exposing
              old ways of working that no longer fit how customers actually
              move.
            </p>
            <p className="font-medium text-navy" data-id="element-634">
              The visible symptom is rarely the whole story.
            </p>
            <p data-id="element-635">This page is here to help you see the patterns underneath it.</p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6" data-id="element-636">
            <div className="flex flex-col" data-id="element-637">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-638">
                <span data-id="element-639">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-640" />
              </Link>
              <p className="text-sm text-text-muted mt-2" data-id="element-641">
                45 minutes. No pitch. Just clarity.
              </p>
            </div>

            <div className="flex flex-col" data-id="element-642">
              <a href="#six-taxes" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-643">
                <span data-id="element-644">Jump to the Six Structural Taxes</span>
                <ChevronDown className="w-4 h-4" data-id="element-645" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Where revenue usually gets stuck, lost, or missed */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-646">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-647">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-648">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-649">
              Where revenue usually gets stuck, lost, or missed
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-650">
            <p className="text-text" data-id="element-651">
              Revenue usually breaks down where the journey no longer matches
              reality.
            </p>
            <p className="text-text" data-id="element-652">That often looks like:</p>

            <ul className="space-y-3 pl-1" data-id="element-653">
              {['the wrong customers get sold', 'leads or customers stall in the middle', 'handoffs break between teams, stages, or systems', 'what gets promised drifts from what delivery can actually support', 'pricing or packaging no longer fits the customer or the work', 'churn starts earlier than leadership thinks', 'expansion opportunities go unseen', 'leaders cannot get clean enough signal to see what is actually happening'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-654">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-655" />
                  <span className="text-text" data-id="element-656">{item}</span>
                </li>)}
            </ul>

            <p className="text-text font-medium text-navy" data-id="element-657">
              These are not random issues.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-658">
              They are recurring signs that the journey, the handoffs, or the
              assumptions underneath them have not kept pace with growth.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What is usually happening underneath */}
      <section className="py-24 px-6 bg-paper" data-id="element-659">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-660">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-661">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-662">
              What is usually happening underneath
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-663">
            <p className="text-text" data-id="element-664">
              Most of the time, the problem is not sitting neatly inside one
              department.
            </p>
            <p className="text-text" data-id="element-665">
              It sits in the seams — where the customer reality changed, but the
              business kept running old assumptions.
            </p>
            <p className="text-text" data-id="element-666">
              Old qualification logic. Old handoffs. Old packaging. Old
              escalation paths. Old rules about who decides what, when, and with
              what information.
            </p>
            <p className="text-text" data-id="element-667">
              So teams keep solving what they can see locally.
            </p>
            <p className="text-text" data-id="element-668">
              Sales pushes for conversion. Delivery works around what should
              have been cleaner upstream. Customer success carries friction it
              did not create. Leadership keeps stepping in to interpret,
              resolve, and reconnect the dots.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-669">
              The journey still moves.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-670">
              But not cleanly. And not with as much captured revenue as it
              should.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Revenue Journey Graphic */}
      <section className="py-16 px-6 bg-navy" data-id="element-671">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-6xl mx-auto" data-id="element-672">
          <p className="eyebrow text-white/40 mb-6 text-center" data-id="element-673">
            The Revenue Journey
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/sfaH98wD5SnpeERBmaEXg9/Signal_Decay_(16_9).png" alt="Revenue journey signal decay — showing how value degrades across qualification, conversion, handoff, delivery, retention, and expansion" className="w-full h-auto" data-id="element-674" />
        </motion.div>
      </section>

      {/* Six Taxes Intro + Accordion */}
      <section id="six-taxes" className="relative py-24 px-6 grain border-y border-seaglass/20 bg-[#8BA89F26] overflow-hidden" data-id="element-675">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-676" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-677">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-678">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-679">
              The Six Structural Taxes
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-680">
              Which pattern is creating the most drag right now?
            </h2>
            <p className="text-base text-navy font-medium mb-2" data-id="element-681">
              The Six Structural Taxes are not stages of the journey.
            </p>
            <p className="text-base text-navy font-medium mb-2" data-id="element-682">
              They are the recurring ways growth starts breaking it once the
              business outgrows the old logic underneath it.
            </p>
            <p className="text-base text-text-muted mb-2" data-id="element-683">
              You may feel more than one. That is normal.
            </p>
            <p className="text-base text-text-muted mb-2" data-id="element-684">
              The goal is not to label everything.
            </p>
            <p className="text-base text-text-muted" data-id="element-685">
              The goal is to notice which pattern is loudest, where the dead
              ends are, and which problem is most worth fixing first.
            </p>
          </motion.div>

          <div className="space-y-0" data-id="element-686">
            {taxes.map((tax, i) => <motion.div key={tax.anchor} id={tax.anchor} variants={fadeIn} className="border-b border-seaglass/20 last:border-0 scroll-mt-28" data-id="element-687">
                <button onClick={() => setExpandedTax(expandedTax === i ? null : i)} className="w-full py-6 flex items-start justify-between text-left group" data-id="element-688">
                  <div className="flex-1 pr-6" data-id="element-689">
                    <div className="flex items-start space-x-4 mb-2" data-id="element-690">
                      <span className="text-sm font-mono font-semibold text-seaglass uppercase tracking-wider" data-id="element-691">
                        {tax.num}
                      </span>
                      <h3 className="text-2xl font-serif font-semibold text-navy" data-id="element-692">
                        {tax.name}
                      </h3>
                    </div>

                    <p className="text-base text-text ml-10" data-id="element-693">
                      {tax.definition}
                    </p>

                    {expandedTax !== i && <div className="ml-10 mt-3" data-id="element-694">
                        <p className="text-sm text-text-muted italic" data-id="element-695">
                          Feels like: {tax.feelsLike}
                        </p>
                      </div>}
                  </div>

                  <ChevronDown className={`w-5 h-5 text-seaglass flex-shrink-0 transition-transform duration-300 ${expandedTax === i ? 'rotate-180' : ''}`} data-id="element-696" />
                </button>

                {expandedTax === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="pb-6 ml-10" data-id="element-697">
                    <div className="space-y-6" data-id="element-698">
                      <p className="text-sm text-text-muted italic" data-id="element-699">
                        Feels like: {tax.feelsLike}
                      </p>

                      <div data-id="element-700">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-701">
                          Where it tends to show up in the revenue journey
                        </p>
                        <p className="text-base text-text" data-id="element-702">
                          {tax.whereShowsUp}
                        </p>
                      </div>

                      <div data-id="element-703">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-704">
                          What it causes
                        </p>
                        <p className="text-base text-text" data-id="element-705">
                          {tax.whatItCauses}
                        </p>
                      </div>

                      <div data-id="element-706">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-707">
                          What leaders usually misread
                        </p>
                        <p className="text-base text-text" data-id="element-708">
                          {tax.commonMisread}
                        </p>
                      </div>

                      <div data-id="element-709">
                        <p className="text-base font-semibold text-navy mb-2" data-id="element-710">
                          What we would validate
                        </p>
                        <ul className="space-y-2" data-id="element-711">
                          {tax.validate.map((item, k) => <li key={k} className="flex items-start space-x-2 text-base text-text" data-id="element-712">
                              <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-713" />
                              <span data-id="element-714">{item}</span>
                            </li>)}
                        </ul>
                      </div>

                      <p className="text-sm text-text-muted" data-id="element-715">
                        If this feels like your loudest tax,{' '}
                        <Link to="/honest-conversation" className="text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-716">
                          book an Honest Conversation →
                        </Link>
                      </p>

                      <a href="#top" className="inline-flex items-center text-sm text-seaglass hover:text-seaglass-light font-medium transition-colors duration-200" data-id="element-717">
                        Back to top →
                      </a>
                    </div>
                  </motion.div>}
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* What people usually do instead */}
      <section className="py-24 px-6 bg-paper" data-id="element-718">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-719">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-720">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-721">
              What people usually do instead
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-722">
            <p className="text-text" data-id="element-723">
              Most companies do not rethink the journey when growth changes.
            </p>
            <p className="text-text" data-id="element-724">
              They keep running the old version harder.
            </p>
            <p className="text-text" data-id="element-725">
              They push more demand into paths that no longer fit. They layer
              process on top of broken handoffs. They keep selling through
              outdated pricing or packaging. They miss expansion that the
              current model does not surface. They ask teams to work around
              friction the system should have removed.
            </p>
            <p className="text-text" data-id="element-726">That can hide the problem for a while.</p>
            <p className="text-text" data-id="element-727">But it does not remove the dead ends.</p>
            <p className="text-text" data-id="element-728">
              It just asks people and customers to carry them.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-729">
              The better move is to find where the old logic no longer fits,
              where the dead ends are, and which lever creates the most revenue
              when pulled next.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-730">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-731">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-732">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-733">The point</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-734">
              You do not fix everything at once.
            </h2>
            <p className="text-body text-text mb-3" data-id="element-735">
              You identify which pattern is creating the most lost or missed
              revenue right now, find the constraint behind it, and redesign the
              right parts in the right order.
            </p>
            <p className="text-body text-text mb-3" data-id="element-736">
              That might mean a cleaner handoff. A tighter qualification rule. A
              pricing or packaging change. A better expansion path. A visibility
              fix that makes the real decision obvious. A dead end removed so
              customers can keep moving.
            </p>
            <p className="text-body text-text font-medium" data-id="element-737">
              The point is not a bigger plan.
            </p>
            <p className="text-body text-text font-medium" data-id="element-738">
              The point is finding the next domino.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="mt-8" data-id="element-739">
            <Link to="/how-we-fix-it" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-740">
              <span data-id="element-741">See how we fix it</span>
              <ArrowRight className="w-4 h-4" data-id="element-742" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* What to do next */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-743">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-744">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-745">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-746">
              What to do next
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8" data-id="element-747">
            <p className="text-text" data-id="element-748">
              You do not need a full-company reset to move forward.
            </p>
            <p className="text-text" data-id="element-749">
              You need a clearer read on where the journey no longer fits, what
              is creating the drag, and which change would create the most
              leverage next.
            </p>

            <p className="text-text" data-id="element-750">In 45 minutes, we will:</p>

            <ul className="space-y-3 pl-1" data-id="element-751">
              {['pressure-test what you are seeing', 'clarify where the drag or missed opportunity may actually be coming from', 'decide whether this looks structural or not', 'make the next step clearer'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-752">
                  <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-753" />
                  <span className="text-text" data-id="element-754">{item}</span>
                </li>)}
            </ul>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-755">
              <p className="eyebrow mb-2 text-seaglass" data-id="element-756">Possible outcomes</p>
              <ul className="space-y-2 text-sm text-text" data-id="element-757">
                <li className="flex items-start space-x-2" data-id="element-758">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-759" />
                  <span data-id="element-760">You are fine. Keep pushing.</span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-761">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-762" />
                  <span data-id="element-763">This is real, but it is not the right timing yet.</span>
                </li>
                <li className="flex items-start space-x-2" data-id="element-764">
                  <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1" data-id="element-765" />
                  <span data-id="element-766">A Revenue Journey Diagnostic is warranted.</span>
                </li>
              </ul>
            </div>

            <p className="text-text font-medium" data-id="element-767">
              If the issue is not structural, I will tell you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-768">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-769">
          <BlueprintGrid data-id="element-770" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-771">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-772">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-773">
              Spot the pattern.
            </span>{' '}
            <span className="text-white" data-id="element-774">
              Then decide what is actually worth fixing.
            </span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-775">
            You do not need a bigger plan first. You need a clearer read.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-776">
            <span data-id="element-777">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-778" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-779">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}